import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { formatDatumIdo } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Fokonyv, Gomb, HibaUzenet, OldalFej, Ures } from '../ui/elemek.tsx';

const EREDMENY: Record<string, string> = {
  ok: 'Újra sorba állítva.',
  bizonytalan_sms: 'Ez az SMS kimehetett, ezért nem küldjük újra. Ellenőrizd a SeeMe-fiók kiküldési listájában.',
  nem_hibas: 'Már nincs hibás állapotban.',
  nincs: 'Nem található.',
};
const ALLAPOTOK = [['var', 'Várakozik'], ['folyamatban', 'Folyamatban'], ['vegleges_hiba', 'Végleges hiba']] as const;

export default function SuperOutbox() {
  const kliens = useQueryClient();
  const [eredmenyek, setEredmenyek] = useState<Record<string, string>>({});

  const osszesito = useQuery({
    queryKey: ['super-outbox-osszesito'],
    queryFn: async () => {
      const mostantol = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const db = await Promise.all(ALLAPOTOK.map(async ([allapot]) => {
        const { count, error } = await supabase.from('outbox').select('id', { count: 'exact', head: true }).eq('allapot', allapot).gte('created_at', mostantol);
        if (error) throw error;
        return count ?? 0;
      }));
      return db;
    },
  });

  const lista = useQuery({
    queryKey: ['super-outbox'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('outbox')
        .select('id, created_at, updated_at, csatorna, sablon, cimzett, hiba, probalkozas, kuldes_megkezdve_at, lead_id, iroda:offices(nev)')
        .eq('allapot', 'vegleges_hiba')
        .order('updated_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  const ujrakuldes = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('outbox_ujrakuldes', { p_id: id });
      if (error) throw error;
      return { id, eredmeny: data };
    },
    onSuccess: async ({ id, eredmeny }) => {
      setEredmenyek((e) => ({ ...e, [id]: EREDMENY[eredmeny] ?? eredmeny }));
      await Promise.all([kliens.invalidateQueries({ queryKey: ['super-outbox'] }), kliens.invalidateQueries({ queryKey: ['super-outbox-osszesito'] })]);
    },
  });

  return (
    <div className="grid gap-4">
      <OldalFej cim="Kiküldések" leiras="A levelek és SMS-ek állapota az elmúlt 24 órában, és a végleges hibák." />
      <HibaUzenet hiba={osszesito.error} />
      {osszesito.data && (
        <div className="grid grid-cols-3 gap-3">
          {ALLAPOTOK.map(([kulcs, cimke], i) => (
            <div key={kulcs} className="grid gap-2 rounded-[18px] border border-vonal bg-hatter p-4">
              <span className={`szam-nagy text-3xl ${kulcs === 'vegleges_hiba' && osszesito.data[i]! > 0 ? 'text-masodlagos' : ''}`}>{osszesito.data[i]}</span>
              <span className="text-sm text-szoveg-halk">{cimke} (24 óra)</span>
            </div>
          ))}
        </div>
      )}
      <h2 className="mt-2 text-lg font-medium tracking-[-0.015em]">Végleges hibák</h2>
      <HibaUzenet hiba={lista.error} />
      <HibaUzenet hiba={ujrakuldes.error} />
      {lista.isPending && <Betolto />}
      {lista.data && lista.data.length === 0 && <Ures cim="Nincs hibás üzenet" szoveg="Minden üzenet rendben kiment vagy várakozik." />}
      {lista.data && lista.data.length > 0 && (
        <Fokonyv><ul className="divide-y divide-vonal">
          {lista.data.map((o) => (
            <li key={o.id}>
              <div className="grid gap-2 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="grid min-w-0 gap-0.5">
                    <span className="break-all font-medium">{o.cimzett}</span>
                    <span className="text-sm text-szoveg-halk">{o.csatorna}, {o.sablon}{o.iroda ? `, ${o.iroda.nev}` : ''}, {o.probalkozas}. próbálkozás</span>
                    <span className="text-sm text-szoveg-halk">Utoljára: {formatDatumIdo(o.updated_at)}</span>
                  </div>
                  <Gomb valtozat="masodlagos" toltes={ujrakuldes.isPending && ujrakuldes.variables === o.id} onClick={() => ujrakuldes.mutate(o.id)}>Újraküldés</Gomb>
                </div>
                {o.hiba && <p className="break-words rounded-xl bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{o.hiba}</p>}
                {o.lead_id && <Link className="text-sm text-kiemelt underline" to={`/leadek/${o.lead_id}`}>Lead adatlapja</Link>}
                {eredmenyek[o.id] && <p role="status" className="text-sm">{eredmenyek[o.id]}</p>}
              </div>
            </li>
          ))}
        </ul></Fokonyv>
      )}
    </div>
  );
}
